import { Globe, InfoIcon, Pencil, Trash2, X } from 'lucide-react';
import React, { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import {
  ProofreadingRule,
  ProofreadingRuleType,
} from '../../../services/types/proofreading-types';

interface RuleCardProps {
  rule: ProofreadingRule;
  onEdit?: () => void;
  onDelete?: () => void;
  onToggleEnabled?: (enabled: boolean) => void;
}

const typeColors = {
  [ProofreadingRuleType.GRAMMAR]: 'bg-blue-100 text-blue-800',
  [ProofreadingRuleType.LEGAL]: 'bg-purple-100 text-purple-800',
  [ProofreadingRuleType.CONTENT]: 'bg-green-100 text-green-800',
  [ProofreadingRuleType.FORMATTING]: 'bg-amber-100 text-amber-800',
  [ProofreadingRuleType.STYLE]: 'bg-indigo-100 text-indigo-800',
};

export const RuleCard: React.FC<RuleCardProps> = ({
  rule,
  onEdit,
  onDelete,
  onToggleEnabled,
}) => {
  const isGlobal = rule.is_global;
  const isEditable = !isGlobal;
  const [promptOpen, setPromptOpen] = useState(false);

  return (
    <Card
      className={`h-full ${isGlobal ? 'border-blue-200 bg-blue-50/30' : ''}`}
    >
      <CardHeader className="p-4 pb-2 flex flex-row items-start justify-between space-y-0">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h3 className="font-medium">{rule.title}</h3>
            {isGlobal && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1">
                      <Badge
                        variant="secondary"
                        className="bg-blue-100 text-blue-800 flex items-center gap-1"
                      >
                        <Globe className="h-3 w-3" />
                        <span>Global</span>
                      </Badge>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">
                      Global rules are enabled for all firms
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <Badge
            variant="outline"
            className={`${typeColors[rule.rule_type]} capitalize`}
          >
            {rule.rule_type.replace('_', ' ')}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          {onToggleEnabled && !isGlobal && (
            <Switch
              checked={rule.enabled}
              onCheckedChange={onToggleEnabled}
              aria-label={`${rule.enabled ? 'Disable' : 'Enable'} rule`}
            />
          )}

          {!isGlobal && onEdit && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit();
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Edit rule</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {!isGlobal && onDelete && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-red-500"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete();
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Delete rule</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-2">
        <p className="text-sm text-gray-600 line-clamp-2">{rule.description}</p>

        <div className="mt-3 flex items-center gap-1">
          <Popover open={promptOpen} onOpenChange={setPromptOpen}>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm" className="px-0 h-6">
                <InfoIcon className="h-3.5 w-3.5 text-gray-500 mr-1" />
                <span className="text-xs text-gray-500">View Prompt</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-72 p-0 max-h-96 overflow-auto"
              onPointerDownOutside={(e) => e.preventDefault()}
            >
              <div className="p-3 pb-2">
                <div className="flex justify-between items-start">
                  <h4 className="font-medium text-sm text-gray-900">
                    Rule Details
                  </h4>
                  <div className="flex gap-2 items-center">
                    <div
                      className={`text-xs font-medium rounded-full px-1.5 py-0.5 ${
                        isGlobal
                          ? 'bg-blue-50 text-blue-700'
                          : 'bg-purple-50 text-purple-700'
                      }`}
                    >
                      {isGlobal ? 'Global' : 'Firm-Specific'}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto w-auto p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPromptOpen(false);
                      }}
                    >
                      <X className="h-3.5 w-3.5 text-gray-500" />
                      <span className="sr-only">Close</span>
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  Created: {new Date(rule.created_at).toLocaleDateString()}
                </p>
                <Separator className="my-2" />
                <h5 className="text-xs uppercase font-medium text-gray-700 mb-0.5">
                  Description
                </h5>
                <p className="text-sm text-gray-800 whitespace-pre-wrap mb-2">
                  {rule.description}
                </p>
              </div>

              <div className="bg-gray-50 p-3 pt-2 rounded-b-md">
                <h5 className="text-xs uppercase font-medium text-gray-700 mb-0.5">
                  AI Prompt
                </h5>
                <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {rule.prompt}
                </p>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </CardContent>
    </Card>
  );
};
